import { createSelector } from '@reduxjs/toolkit';
import { AppState } from '~store/modules';
import {
  CommonDebuggerVariablesKey,
  IBreakpoint,
  IBreakpointsByFile,
  IDebuggerData,
  IDebuggerSession,
  IEnhancedBreakpoint,
} from '~store/modules/debugger/types';
import { FileFolderType, ITab } from '@qodly/shared';
import { getDebuggerTabName } from '~store/modules/debugger/utils';
import { selectTabs } from './explorer';

export const selectDebuggerMeta = createSelector(
  (state: AppState) => state.debugger,
  (state) => state.meta,
);

export const selectDebuggerData = createSelector(
  (state: AppState) => state.debugger,
  (state) => state.list,
);

export const selectDebuggerSessions = createSelector(
  (state: AppState) => state.debugger,
  (state) => state.sessions,
);

export const selectDebuggerSessionsCount = createSelector(
  selectDebuggerSessions,
  (sessions) => sessions.length,
);

export const findOne = (
  list: IDebuggerData[],
  filePath: string,
  methodName: string,
  className?: string,
) =>
  list.find(
    (item) =>
      item.filePath === filePath &&
      item.methodName === methodName &&
      (!className || item.className === className),
  );

export const findSession = (sessions: IDebuggerSession[], threadId: number) =>
  sessions.find((item) => item.threadId === threadId);

export const findSessionByPath = (
  sessions: IDebuggerSession[],
  tabPath: string,
) => sessions.find((item) => item.tabPath === tabPath);

export const selectAllBreakpoints = createSelector(selectDebuggerData, (list) =>
  list.reduce((acc: IEnhancedBreakpoint[], cur) => {
    const savedBreakpoints = cur.breakpoints.saved.locations.map(
      (location) => ({
        ...location,
        offset: cur.breakpoints.saved.offset,
        fileType: cur.fileType,
        filePath: cur.filePath,
        methodName: cur.methodName,
        className: cur.className,
        ext: cur.ext,
      }),
    );

    return [...acc, ...savedBreakpoints] as IEnhancedBreakpoint[];
  }, []),
);

export const selectBreakpointsByFile = createSelector(
  selectDebuggerData,
  (list) =>
    list.reduce((acc: IBreakpointsByFile[], cur) => {
      const sortByAscOrder = (a: IEnhancedBreakpoint, b: IEnhancedBreakpoint) =>
        a.line + (a.offset || 0) - (b.line + (b.offset || 0));

      const savedBreakpoints = cur.breakpoints.saved.locations.map(
        (location) => ({
          ...location,
          offset: cur.breakpoints.saved.offset,
          fileType: cur.fileType,
          filePath: cur.filePath,
          methodName: cur.methodName,
          className: cur.className,
          ext: cur.ext,
        }),
      );

      if (savedBreakpoints.length === 0) {
        return acc;
      }

      let found = false;
      const result = acc.map((group) => {
        if (group.file.path === cur.filePath) {
          found = true;
          return {
            ...group,
            breakpoints: [...group.breakpoints, ...savedBreakpoints].sort(
              sortByAscOrder,
            ),
          };
        }
        return group;
      }) as IBreakpointsByFile[];

      return found
        ? result
        : ([
            ...acc,
            {
              file: {
                path: cur.filePath,
                type: cur.fileType,
                methodName: cur.methodName,
                className: cur.className,
                ext: cur.ext,
              },
              breakpoints: savedBreakpoints.sort(sortByAscOrder),
            },
          ] as IBreakpointsByFile[]);
    }, []),
);

export const selectAllBreakpointsCount = createSelector(
  selectAllBreakpoints,
  (breakpoints) => breakpoints.length,
);

export const selectEnabledBreakpointsCount = createSelector(
  selectAllBreakpoints,
  (breakpoints) => breakpoints.filter(({ disabled }) => !disabled).length,
);

export const selectBreakpoints = (filePath: string, methodName: string) =>
  createSelector(
    selectDebuggerData,
    (list) =>
      findOne(list, filePath, methodName)?.breakpoints.saved.locations || [],
  );

export const selectDraftBreakpoints = (filePath: string, methodName: string) =>
  createSelector(
    selectDebuggerData,
    (list) =>
      findOne(list, filePath, methodName)?.breakpoints.draft.locations || [],
  );

export const selectClassBreakpoints = (filePath: string) =>
  createSelector(selectDebuggerData, (list) => {
    return (
      list
        .filter((item) => item.filePath === filePath)
        .reduce(
          (acc: IBreakpoint[], cur) => [
            ...acc,
            ...cur.breakpoints.saved.locations.map((location) => ({
              ...location,
              line: location.line + (cur.breakpoints.saved.offset || 0),
            })),
          ],
          [],
        )
        // NOTE: Below sort fixes a rendering issue on classes
        // when having 2 successive breakpoints & deleting the line of the first one
        .sort((a, b) => a.line - b.line)
    );
  });

export const selectDraftClassBreakpoints = (filePath: string) =>
  createSelector(selectDebuggerData, (list) => {
    return list
      .filter((item) => item.filePath === filePath)
      .reduce(
        (acc: IBreakpoint[], cur) => [
          ...acc,
          ...cur.breakpoints.draft.locations.map((location) => ({
            ...location,
            line: location.line + (cur.breakpoints.draft.offset || 0),
          })),
        ],
        [],
      )
      .sort((a, b) => a.line - b.line);
  });

export const selectHighlightedLines = (threadId: number) =>
  createSelector(selectDebuggerSessions, (sessions) => {
    const session = findSession(sessions, threadId);
    return session?.stopped ? session.highlightedLines : null;
  });

export const selectActiveLine = (threadId: number) =>
  createSelector(selectDebuggerSessions, (sessions) => {
    const session = findSession(sessions, threadId);
    return session?.stopped ? session.activeLine : null;
  });

export const selectExpressions = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.expressions || [],
  );

export const selectStoppedFromDebugSession = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.stopped || false,
  );

export const selectSourceByPath = (filePath: string, methodName: string) =>
  createSelector(
    selectDebuggerData,
    (list) => findOne(list, filePath, methodName)?.source,
  );

export const selectThreadIdByPath = (tabPath: string) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSessionByPath(sessions, tabPath)?.threadId || -1,
  );

export const selectSourceByThreadId = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.currentStackFrame?.source,
  );

export const selectCurrentFrameByThreadId = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.currentStackFrame,
  );

export const selectDebuggerChannels = createSelector(
  selectDebuggerSessions,
  (sessions) =>
    sessions.map(({ channelId, threadId }) => ({
      id: channelId,
      threadId,
    })),
);

export const selectStackFrames = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.stackFrames || [],
  );

export const selectCommonVariables = (
  threadId: number,
  key: CommonDebuggerVariablesKey,
) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.variables?.[key] || [],
  );

export const selectCallChainVariables = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.variables?.callchain || [],
  );

export const selectExpressionsVariables = (threadId: number) =>
  createSelector(
    selectDebuggerSessions,
    (sessions) => findSession(sessions, threadId)?.variables?.expressions || [],
  );

export const selectClassOffset = (
  filePath: string,
  type: keyof IDebuggerData['breakpoints'] = 'saved',
) =>
  createSelector(selectDebuggerData, (list) => {
    const found = list.find((item) => item.filePath === filePath);
    return found?.breakpoints[type].offset || 0;
  });

export const selectExceptions = (
  threadId: number,
  fileType: FileFolderType.CLASS | FileFolderType.METHOD,
  methodName: string,
  className?: string,
) =>
  createSelector(selectDebuggerSessions, (sessions) => {
    const exceptions = findSession(sessions, threadId)?.exceptions || [];
    const isClass = fileType === FileFolderType.CLASS;
    const location =
      isClass && className ? `${className}.${methodName}` : methodName;
    const rawExceptions = location
      ? exceptions.filter((exception) => exception.location === location)
      : exceptions;
    return rawExceptions.map((exception) => ({
      ...exception,
      locationLine: isClass
        ? exception.locationLine
        : exception.locationLine + 1, // for methods case add 1 line for the "//% attributes" line
    }));
  });

export const selectDebuggers = createSelector(
  selectDebuggerSessions,
  (sessions) =>
    sessions.map(
      ({ threadId, currentStackFrame: { source }, tabPath: path }) =>
        ({
          name: getDebuggerTabName(threadId, source),
          path,
          type: FileFolderType.DEBUGGER,
          content: source?.content,
          flags: {},
          extra: { source },
        }) as ITab,
    ),
);
